// PATH: src/components/catalog/forms/VisualFields.tsx
/**
 * Visuels de l'univers 3D élève (programme, matière) : couleurs, texture,
 * relief (bump), anneaux, rayon d'orbite. Les images se téléversent sur le
 * service d'upload (`useUploadApi`) ou se collent par URL.
 */
import * as React from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/CloseRounded";
import UploadIcon from "@mui/icons-material/FileUploadOutlined";
import ImageIcon from "@mui/icons-material/ImageOutlined";
import { useUploadApi } from "../../../api/uploadApi";
import { tokens } from "../../../theme/theme";
import { useToast } from "../../ui";
import { emptyToNull, isHexColor, parseOptionalNumber } from "../catalogModel";
import { PlanetPreview } from "../PlanetPreview";

export interface VisualValues {
  colorPrimary: string;
  colorSecondary: string;
  textureUrl: string;
  bumpUrl: string;
  hasRings: boolean;
  orbitRadius: string;
}

type VisualSource = {
  color_primary?: string | null;
  color_secondary?: string | null;
  texture_url?: string | null;
  bump_url?: string | null;
  has_rings?: boolean | null;
  orbit_radius?: number | null;
};

export const visualsFrom = (x?: VisualSource | null): VisualValues => ({
  colorPrimary: x?.color_primary ?? "",
  colorSecondary: x?.color_secondary ?? "",
  textureUrl: x?.texture_url ?? "",
  bumpUrl: x?.bump_url ?? "",
  hasRings: Boolean(x?.has_rings),
  orbitRadius: x?.orbit_radius != null ? String(x.orbit_radius) : "",
});

export const visualsPayload = (v: VisualValues) => ({
  color_primary: emptyToNull(v.colorPrimary),
  color_secondary: emptyToNull(v.colorSecondary),
  texture_url: emptyToNull(v.textureUrl),
  bump_url: emptyToNull(v.bumpUrl),
  has_rings: v.hasRings,
  orbit_radius: parseOptionalNumber(v.orbitRadius),
});

export const visualErrors = (v: VisualValues) => ({
  colorPrimary: v.colorPrimary.trim() && !isHexColor(v.colorPrimary) ? "Format #RRGGBB." : "",
  colorSecondary: v.colorSecondary.trim() && !isHexColor(v.colorSecondary) ? "Format #RRGGBB." : "",
  orbitRadius: Number.isNaN(parseOptionalNumber(v.orbitRadius)) ? "Un nombre." : "",
});

export const hasVisualErrors = (v: VisualValues) => Object.values(visualErrors(v)).some(Boolean);

// ---------------------------------------------------------------------------

export const ColorField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  helperText?: string;
}> = ({ label, value, onChange, error, helperText }) => {
  const valid = isHexColor(value);
  return (
    <TextField
      label={label}
      value={value}
      placeholder="#RRGGBB"
      onChange={(e) => onChange(e.target.value)}
      error={Boolean(error)}
      helperText={error || helperText || " "}
      fullWidth
      slotProps={{
        htmlInput: { className: "mono", spellCheck: false },
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Tooltip title="Choisir une couleur">
                <Box
                  component="label"
                  sx={{
                    position: "relative",
                    width: 22,
                    height: 22,
                    borderRadius: 1,
                    cursor: "pointer",
                    bgcolor: valid ? value : "transparent",
                    border: valid ? `1px solid ${tokens.borderStrong}` : `1.5px dashed ${tokens.borderStrong}`,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    component="input"
                    type="color"
                    value={valid && value.length === 7 ? value : "#888888"}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
                    sx={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer" }}
                  />
                </Box>
              </Tooltip>
            </InputAdornment>
          ),
          endAdornment: value ? (
            <InputAdornment position="end">
              <IconButton size="small" aria-label="Effacer la couleur" onClick={() => onChange("")} edge="end">
                <CloseIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      }}
    />
  );
};

const ImageField: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  uploadPath: string;
  onBusy: (busy: boolean) => void;
}> = ({ label, value, onChange, uploadPath, onBusy }) => {
  const { uploadMedia } = useUploadApi();
  const toast = useToast();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [broken, setBroken] = React.useState(false);

  React.useEffect(() => setBroken(false), [value]);

  const pick = async (file?: File | null) => {
    if (!file) return;
    setUploading(true);
    onBusy(true);
    try {
      onChange(await uploadMedia(file, uploadPath));
    } catch {
      toast.error(`Échec du téléversement : ${label.toLowerCase()}.`);
    } finally {
      setUploading(false);
      onBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <Stack direction="row" spacing={1.5} alignItems="flex-start">
      <Box
        sx={{
          width: 40,
          height: 40,
          mt: 0,
          borderRadius: 1.5,
          flexShrink: 0,
          display: "grid",
          placeItems: "center",
          bgcolor: tokens.surfaceMuted,
          border: `1px solid ${tokens.border}`,
          color: "text.disabled",
          backgroundImage: value && !broken ? `url("${value}")` : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        {(!value || broken) && <ImageIcon fontSize="small" />}
        {value && <img src={value} alt="" hidden onError={() => setBroken(true)} />}
      </Box>
      <TextField
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://…"
        fullWidth
        helperText={broken ? "Image introuvable à cette adresse." : " "}
        error={broken}
        slotProps={{
          input: {
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label={`Retirer : ${label}`} onClick={() => onChange("")} edge="end">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          },
        }}
      />
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={(e) => void pick(e.target.files?.[0])} />
      <Button
        variant="outlined"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        startIcon={uploading ? <CircularProgress size={14} /> : <UploadIcon />}
        sx={{ flexShrink: 0, height: 40 }}
      >
        Téléverser
      </Button>
    </Stack>
  );
};

// ---------------------------------------------------------------------------

interface Props {
  value: VisualValues;
  onChange: (v: VisualValues) => void;
  /** Dossier du service d'upload : « embed/programs », « embed/subjects ». */
  uploadPath: string;
  showErrors: boolean;
  onBusyChange: (busy: boolean) => void;
}

export const VisualFields: React.FC<Props> = ({ value, onChange, uploadPath, showErrors, onBusyChange }) => {
  const set = (patch: Partial<VisualValues>) => onChange({ ...value, ...patch });
  const errors = visualErrors(value);
  const err = (k: keyof typeof errors) => (showErrors ? errors[k] : "");
  const busyCount = React.useRef(0);
  const onBusy = (b: boolean) => {
    busyCount.current += b ? 1 : -1;
    onBusyChange(busyCount.current > 0);
  };

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ pb: 1 }}>
        <PlanetPreview
          look={{
            color_primary: isHexColor(value.colorPrimary) ? value.colorPrimary : null,
            color_secondary: isHexColor(value.colorSecondary) ? value.colorSecondary : null,
            texture_url: value.textureUrl || null,
            has_rings: value.hasRings,
          }}
          size={72}
        />
        <Typography variant="body2" color="text.secondary">
          Aperçu indicatif de l'astre dans l'univers de l'élève. Sans texture, il est peint avec le dégradé des deux
          couleurs.
        </Typography>
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0, sm: 2 }}>
        <ColorField
          label="Couleur principale"
          value={value.colorPrimary}
          onChange={(v) => set({ colorPrimary: v })}
          error={err("colorPrimary")}
        />
        <ColorField
          label="Couleur secondaire"
          value={value.colorSecondary}
          onChange={(v) => set({ colorSecondary: v })}
          error={err("colorSecondary")}
        />
      </Stack>
      <ImageField
        label="Texture"
        value={value.textureUrl}
        onChange={(v) => set({ textureUrl: v })}
        uploadPath={uploadPath}
        onBusy={onBusy}
      />
      <ImageField
        label="Relief (bump map)"
        value={value.bumpUrl}
        onChange={(v) => set({ bumpUrl: v })}
        uploadPath={uploadPath}
        onBusy={onBusy}
      />
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <FormControlLabel
          sx={{ mt: 0.75, flex: 1 }}
          control={<Switch checked={value.hasRings} onChange={(e) => set({ hasRings: e.target.checked })} />}
          label="Anneaux"
        />
        <TextField
          label="Rayon d'orbite"
          value={value.orbitRadius}
          onChange={(e) => set({ orbitRadius: e.target.value })}
          error={Boolean(err("orbitRadius"))}
          helperText={err("orbitRadius") || "Optionnel"}
          sx={{ width: 170 }}
          slotProps={{ htmlInput: { inputMode: "decimal" } }}
        />
      </Stack>
    </Stack>
  );
};

export default VisualFields;
